import React from 'react';
import MUIToggleButton from '@material-ui/lab/ToggleButton';
import MUIToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';

import { Text } from '../Typography';
import { makeStyles } from '../styles';
import cx from 'classnames';

type Prop<TValue> = {
  value: TValue | null;
  disabled?: boolean;
  vertical?: boolean;
  fullWidth?: boolean;
  options: Array<{
    value: TValue;
    label?: string;
    component?: React.ReactElement;
    disabled?: boolean;
  }>;
  onChange: (value: TValue | null) => void;
  version?: '1' | '2';
  className?: string;
  rootStyles?: string;
  toggleButtonSelected?: string;
};

const useStyles = makeStyles<Prop<any>>(theme => ({
  root: props => {
    const v2Styles =
      props.version === '2'
        ? {
            borderRadius: '8px',
            border: `1px solid var(--color-LightSkyBlue)`,
            padding: '1px',
          }
        : {};
    if (props.fullWidth) {
      return {
        width: '100%',
        display: 'flex',
        flexDirection: props.vertical ? 'column' : 'row',
        border: props.vertical ? `1px solid ${theme.colors.gray20}` : 'none',
        ...v2Styles,
      };
    } else {
      return { ...v2Styles };
    }
  },
  button: props => {
    const styles: any = {
      height: 32,
      paddingLeft: 16,
      paddingRight: 16,
      borderColor: theme.colors.gray20,
      '&:disabled': {
        opacity: 0.4,
      },
    };
    const v2Styles =
      props.version === '2'
        ? {
            borderTop: 0,
            borderLeft: 0,
            borderRight: 0,
            borderBottom: 0,
            borderRadius: '8px !important',
            fontWeight: 'bold',
            color: 'black',
          }
        : {};

    if (props.vertical) {
      styles.borderTop = 0;
      styles.borderLeft = 0;
      styles.borderRight = 0;
      styles['&:last-child'] = {
        borderBottom: 0,
      };
    }

    if (props.fullWidth) {
      styles.flexGrow = 1;
    }

    return { ...styles, ...v2Styles };
  },
  buttonSelected: props => {
    return props.version === '2'
      ? {
          backgroundColor: 'var(--color-LightSkyBlue) !important',
          color: 'var(--color-primary) !important',
        }
      : {
          backgroundColor: theme.colors.gray05 + ' !important',
          borderLeftColor: theme.colors.gray20 + ' !important',
          color: theme.colors.blue + ' !important',
        };
  },
}));

const ToggleButtonGroup = function <TValue>(props: Prop<TValue>) {
  const classes = useStyles(props);

  return (
    <MUIToggleButtonGroup
      exclusive
      size="small"
      value={props.value}
      onChange={(_e, value: TValue | null) => props.onChange(value)}
      classes={{ root: props.rootStyles ? cx(classes.root, props.rootStyles) : classes.root }}
    >
      {props.options.map(option => {
        const OptionContents = option.component || <Text>{option.label}</Text>;
        return (
          <MUIToggleButton
            key={String(option.value)}
            value={option.value}
            disabled={option.disabled || props.disabled}
            className={props.className ? cx(classes.button, props.className) : classes.button}
            classes={{
              selected: props.toggleButtonSelected
                ? cx(classes.buttonSelected, props.toggleButtonSelected)
                : classes.buttonSelected,
            }}
          >
            {OptionContents}
          </MUIToggleButton>
        );
      })}
    </MUIToggleButtonGroup>
  );
};

export default ToggleButtonGroup;
